import React, {useEffect, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Box, Flex, Stack, Text, Divider, useMultiStyleConfig} from '@chakra-ui/react'

import {defineMessage, FormattedMessage, useIntl} from 'react-intl'
import {remCalc} from '../../theme/_utils'
import Field from '../field'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {MESSAGE_PROPTYPE} from '../../utils/locale'
import useAddressFields from '../forms/use-address-fields'
import {useCountryLabels} from './use-country-labels'
import LoqateFields from './loqate-fields'

const defaultFormTitleAriaLabel = defineMessage({
    defaultMessage: 'Address Form',
    id: 'use_address_fields.label.address_form',
})

const AccountAddressFieldsWithAutocomplete = ({
    form,
    defaultShowAutocomplete = true,
    prefix = '',
    formTitleAriaLabel = defaultFormTitleAriaLabel,
    variant = 'default',
    setAutocompleteObj,
}) => {
    const {data: customer} = useCurrentCustomer()
    const styles = useMultiStyleConfig('AddressFields', {variant})
    const [showAutocomplete, setShowAutocomplete] = useState(defaultShowAutocomplete)

    const {formatMessage} = useIntl()

    const {getCountries} = useCountryLabels()

    const [countriesArray, setCountriesArray] = useState([])

    const fields = useAddressFields({
        form,
        prefix,
        countries: countriesArray,
    })

    useEffect(() => {
        setCountriesArray(getCountries())

        const values = form.getValues()
        if (values.address1) {
            setShowAutocomplete(false)
        }
    }, [])

    const watcherStateCode = form.watch('stateCode')

    const province = useMemo(() => {
        const stateCode =
            typeof watcherStateCode === 'string' ? watcherStateCode : watcherStateCode?.value

        return fields.stateCode.options?.find((province) => province.value === stateCode)
    }, [])

    const addressFormRef = useRef()

    useEffect(() => {
        if (province) form.setValue('stateCode', province)
        // Focus on the form when the component mounts for accessibility
        addressFormRef?.current?.focus()
    }, [])

    return (
        <Stack
            {...styles.container}
            aria-label={formatMessage(formTitleAriaLabel)}
            ref={addressFormRef}
        >
            <Flex {...styles.gaps} zIndex={10}>
                <Field {...fields.title} />
                <Field {...fields.firstName} />
            </Flex>
            <Flex {...styles.gaps}>
                <Field {...fields.lastName} />
                <Field {...fields.fiscalCodeNumber} isDisabled={true} />
            </Flex>

            <Divider />
            <LoqateFields
                form={form}
                defaultShowAutocomplete={showAutocomplete}
                prefix={prefix}
                variant={variant}
                setAutocompleteObj={setAutocompleteObj}
            />

            <Stack gap={remCalc(8)}>
                <Flex {...styles.gaps}>
                    <Field {...fields.phone} />
                    <Box w="100%" display={{base: 'none', md: 'block'}} />
                </Flex>
                <Text {...styles.deliveryNoteText}>
                    <FormattedMessage
                        defaultMessage="We'll use it for delivery purposes"
                        id="address_fields.label.delivery_purposes_note"
                    />
                </Text>
            </Stack>
            {customer.isRegistered && <Field {...fields.preferred} variant="row" />}
        </Stack>
    )
}

AccountAddressFieldsWithAutocomplete.propTypes = {
    form: PropTypes.object.isRequired,
    prefix: PropTypes.string,
    edit: PropTypes.object,
    fromEdit: PropTypes.bool,
    defaultShowAutocomplete: PropTypes.bool,
    enableEditing: PropTypes.bool,
    formTitleAriaLabel: MESSAGE_PROPTYPE,
    variant: PropTypes.string,
    setAutocompleteObj: PropTypes.func,
}

export default AccountAddressFieldsWithAutocomplete
